import { useEffect, useState } from 'react';
import type { Universe } from '@kaeliux/shared';
import { listUniverses } from '../services/universe.service';
import { getUserProfile } from '../services/profile.service';
import { useUniverseStore } from '../store/universeStore';
import { useAuthStore } from '../store/authStore';
import { Panel } from './ui/Panel';
import { Chip } from './ui/Chip';
import { Skeleton } from './ui/Skeleton';

/**
 * Shown at every login, before race selection (issue #53) — a player can
 * hold progress in more than one universe at once, so which one the current
 * session is playing in isn't part of the profile; it's picked here and
 * kept in `useUniverseStore` (localStorage-backed), then sent as
 * `universeId` on every gameplay request. Universes themselves are never
 * created/managed from inside the game — see `apps/functions/src/scripts/`.
 */
export default function UniverseSelection() {
    const [universes, setUniverses] = useState<Universe[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [enteringId, setEnteringId] = useState<string | null>(null);
    const [enterError, setEnterError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        listUniverses()
            .then((list) => {
                if (!cancelled) setUniverses(list);
            })
            .catch((error: unknown) => {
                if (!cancelled) setLoadError(error instanceof Error ? error.message : 'Failed to load universes');
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleSelect = async (universeId: string) => {
        if (enteringId) return;
        setEnteringId(universeId);
        setEnterError(null);
        const { user } = useAuthStore.getState();
        try {
            // Fetch BOTH the universe's context and the profile scoped to
            // it BEFORE committing anything — `getUserProfile` is called
            // with `universeId` explicitly rather than relying on
            // `useUniverseStore`'s (not-yet-set) `selectedUniverseId`.
            // `loadUniverse` throws for an unknown/uninitialized universe
            // (see its doc comment), so a bad pick never reaches the commit
            // step below.
            await useUniverseStore.getState().loadUniverse(universeId);
            const profile = user ? await getUserProfile(user.uid, universeId) : null;

            // Commit synchronously, back-to-back, with no `await` between
            // them — React 18 batches same-tick updates across stores into
            // ONE render, so `App.tsx` never observes `selectedUniverseId`
            // truthy while `profile` is still the stale, pre-selection
            // value (which used to flash `RaceSelection` for a returning
            // player and, absent a server-side guard, let them click
            // through into a duplicate homeworld — see `createPlanet`'s
            // "already has a homeworld" check).
            useUniverseStore.getState().commitSelectedUniverse(universeId);
            useAuthStore.setState({ profile });
        } catch (error: unknown) {
            setEnterError(error instanceof Error ? error.message : 'Failed to enter that universe. Please try again.');
            setEnteringId(null);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4">
            <Panel variant="glass" padding="lg" className="w-full max-w-3xl">
                <div className="text-center mb-8">
                    <h1 className="text-display text-content-primary mb-2">Choose Your Universe</h1>
                    <p className="text-content-muted text-body">
                        Every commander picks a universe at the start of each session
                    </p>
                </div>

                {loadError && (
                    <p className="mb-4 text-status-danger text-body text-center" role="alert">
                        {loadError}
                    </p>
                )}

                {!universes && !loadError && (
                    <div className="space-y-3">
                        <Skeleton variant="text" className="h-6 w-full" />
                        <Skeleton variant="block" className="h-24 w-full" />
                        <Skeleton variant="block" className="h-24 w-full" />
                    </div>
                )}

                {universes && universes.length === 0 && (
                    <div className="text-center text-content-muted text-body py-8">
                        No universes are open yet. Check back soon.
                    </div>
                )}

                {universes && universes.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {universes.map((universe) => {
                            const isEntering = enteringId === universe.id;
                            return (
                                <button
                                    key={universe.id}
                                    type="button"
                                    data-testid="universe-card"
                                    onClick={() => handleSelect(universe.id)}
                                    disabled={enteringId !== null}
                                    className="relative p-6 rounded-tile border-2 border-space-600 bg-space-800/50 text-left transition-all duration-300 hover:border-space-400 hover:bg-space-700/50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    <h3 className="text-heading text-content-primary mb-4">{universe.name ?? universe.id}</h3>
                                    <div className="flex flex-wrap gap-2">
                                        <Chip tone="neutral">{universe.resourceMultiplier}x Resources</Chip>
                                        <Chip tone="neutral">{universe.speedMultiplier ?? 1}x Speed</Chip>
                                        <Chip tone="neutral">{universe.flightMultiplier ?? 1}x Flight</Chip>
                                    </div>
                                    {isEntering && (
                                        <div className="absolute inset-0 rounded-tile bg-space-900/70 flex items-center justify-center">
                                            <span className="text-content-primary text-body">Entering...</span>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {enterError && (
                    <p className="mt-2 text-status-danger text-body text-center" role="alert">
                        {enterError}
                    </p>
                )}
            </Panel>
        </div>
    );
}
